import Badge from '@/common/components/Badge'
import Box from '@/common/components/Box'
import ButtonLink from '@/common/components/ButtonLink'
import GeoSelectOption from '@/common/components/GeoSelectOption'
import ResumeItem from '@/common/components/ResumeItem'
import { formatCurrency, formatDecimal, formatUnit } from '@/common/utils/formatters'
import { CompanyContractType } from '@/features/privateCompanies/types/companyContract.type'
import { options as priceRuleOptions } from '@/features/publicCompanyLeadsFunnel/constants/priceRuleOptions.constant'
import { FileBoxIcon } from 'lucide-react'

interface ContractProductCategoriesProductItemProps {
  contractProductCategoryProduct: CompanyContractType['contract_product_categories'][number]['contract_product_category_products'][number]
  contractProductCategoryIndex: number
  contractProductCategoryProductIndex: number
  contract?: CompanyContractType
}

export const ContractProductCategoriesProductItem = ({
  contractProductCategoryProduct,
  contractProductCategoryIndex,
  contractProductCategoryProductIndex,
  contract,
}: ContractProductCategoriesProductItemProps) => {
  return (
    <Box className="flex flex-col gap-4 p-3">
      <div className="border-s border-blue-800 px-3 py-2 flex">
        <ButtonLink
          key={contractProductCategoryProduct.id}
          linkTo={`/products/${contractProductCategoryProduct?.product?.id}`}
          operationKlassToCheckPermission='Admin::Products::Show'
          className='underline-offset-2'
          dataTestId='product_clickable_link'
        >
          <Badge
            valueDataTestid={`contract_product_category_${contractProductCategoryIndex}_product_${contractProductCategoryProductIndex}_display_name`}
            Icon={FileBoxIcon}
            iconProps={{ className: 'size-5', strokeWidth: 1 }}
            text={contractProductCategoryProduct?.product?.display_name}
            overwriteClassName
          />
        </ButtonLink>
      </div>

      {contractProductCategoryProduct.global_value_in_unit_for_consumption && (contractProductCategoryProduct.global_value_in_currency || contractProductCategoryProduct.product.unit_for_quantity) && (
          <div className="flex items-center">
            <div className="flex gap-6">
              {contract?.consumption_controlled_by !== 'quantity' && (
                <ResumeItem
                  valueDataTestid={`contract_product_category_${contractProductCategoryIndex}_product_${contractProductCategoryProductIndex}_global_value_in_currency`}
                  label="Cota total em valor"
                  value={
                    contractProductCategoryProduct.global_value_in_currency
                      ? formatCurrency(Number(contractProductCategoryProduct.global_value_in_currency))
                      : ''
                  }
                  labelClassName="text-white-400"
                  valueClassName="text-md"
                />
              )}

              <ResumeItem
                valueDataTestid={`contract_product_category_${contractProductCategoryIndex}_product_${contractProductCategoryProductIndex}_global_value_in_unit_for_consumption`}
                label="Cota total em quantidade"
                value={
                  contractProductCategoryProduct.global_value_in_unit_for_consumption
                    ? formatUnit(
                      formatDecimal(Number(contractProductCategoryProduct.global_value_in_unit_for_consumption), 2),
                      contractProductCategoryProduct.product?.unit_for_quantity,
                    )
                    : ''
                }
                labelClassName="text-white-400"
                valueClassName="text-md"
              />
            </div>
          </div>
        )
      }

      <div className="flex gap-6">
        <ResumeItem
          valueDataTestid={`contract_product_category_${contractProductCategoryIndex}_product_${contractProductCategoryProductIndex}_price_rule`}
          label="Regras de preço"
          value={
            contractProductCategoryProduct.price_rule
              ? priceRuleOptions?.find((option) => option.value === contractProductCategoryProduct.price_rule)?.label ||
              ''
              : ''
          }
          labelClassName="text-white-400"
          valueClassName="text-md"
        />

        {contractProductCategoryProduct.price_rule === 'fixed' &&
          !contractProductCategoryProduct?.contract_product_category_product_prices?.some((price) => price.geo) && (
            <ResumeItem
              valueDataTestid={`contract_product_category_${contractProductCategoryIndex}_product_${contractProductCategoryProductIndex}_fixed_price`}
              label="Preço fixo a ser praticado"
              value={
                contractProductCategoryProduct.contract_product_category_product_prices?.[0]?.value
                  ? formatCurrency(
                    Number(contractProductCategoryProduct.contract_product_category_product_prices?.[0]?.value),
                  )
                  : ''
              }
              valueClassName="text-md"
            />
          )}
      </div>

      {contractProductCategoryProduct.price_rule === 'fixed' &&
        !!contractProductCategoryProduct.contract_product_category_product_prices?.length &&
        !!contractProductCategoryProduct?.contract_product_category_product_prices?.some((price) => price.geo) && (
          <div className="flex flex-col gap-4">
            {contractProductCategoryProduct.contract_product_category_product_prices?.map((productPrice, index) => (
              <Box key={productPrice.id} className="p-3">
                <div className="border-s border-blue-400 flex items-center gap-4 px-3">
                  {productPrice.geo && (
                    <ResumeItem
                      valueDataTestid={`contract_product_category_${contractProductCategoryIndex}_product_${contractProductCategoryProductIndex}_price_${index}_geo`}
                      label="Região de atuação"
                      value={<GeoSelectOption geo={productPrice.geo} />}
                      labelClassName="text-white-400"
                      valueClassName="text-md"
                    />
                  )}

                  <ResumeItem
                    valueDataTestid={`contract_product_category_${contractProductCategoryIndex}_product_${contractProductCategoryProductIndex}_price_${index}_value`}
                    label="Valor a ser praticado"
                    value={productPrice.value ? formatCurrency(Number(productPrice.value)) : ''}
                    labelClassName="text-white-400"
                    valueClassName="text-md"
                  />
                </div>
              </Box>
            ))}
          </div>
        )}
    </Box>
  )
}
